// ---------------------------------------------------------------------------
// C3: AI Bar Advise Panel
// ---------------------------------------------------------------------------
// "Should I..." → recommendation title + reasoning (2-3 sentences)
// + action buttons. Framed as validating user's intuition.
// ---------------------------------------------------------------------------
import { useNavigate } from "react-router";
import { Sparkles, ThumbsUp, ThumbsDown, ArrowRight, AlertCircle } from "lucide-react";
import { Skeleton } from "@/components/skeleton";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AdviseResult {
  recommendation: "yes" | "no" | "maybe";
  title: string;
  reasoning: string;
  confidence: number; // 0-100
  actions?: Array<{ label: string; href?: string }>;
}

interface AdvisePanelProps {
  result: AdviseResult;
  isLoading: boolean;
  error?: string | null;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function AdvisePanel({ result, isLoading, error }: AdvisePanelProps) {
  const navigate = useNavigate();

  if (error) {
    return (
      <div className="p-4">
        <div className="flex items-center gap-2 text-sm text-[var(--color-destructive)]">
          <AlertCircle className="h-4 w-4" />
          {error}
        </div>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Skeleton className="h-7 w-32 rounded-full" />
        </div>
        <div className="space-y-2">
          <Skeleton className="h-5 w-2/3" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-full" />
          <Skeleton className="h-4 w-2/3" />
        </div>
        <div className="flex items-center gap-2">
          <Skeleton className="h-3 w-16" />
          <Skeleton className="h-1.5 flex-1 rounded-full" />
          <Skeleton className="h-3 w-10" />
        </div>
      </div>
    );
  }

  const recommendationConfig = {
    yes: { icon: ThumbsUp, color: "text-[var(--color-success)] bg-[var(--color-success)]/5", label: "Yes, go for it" },
    no: { icon: ThumbsDown, color: "text-[var(--color-destructive)] bg-[var(--color-destructive)]/5", label: "Not recommended" },
    maybe: { icon: Sparkles, color: "text-[var(--color-warning)] bg-[var(--color-warning)]/5", label: "It depends" },
  };

  const config = recommendationConfig[result.recommendation];
  const RecIcon = config.icon;

  return (
    <div className="p-4 max-h-80 overflow-y-auto">
      {/* AI label */}
      <div className="flex items-center gap-1.5 mb-3">
        <Sparkles className="h-3.5 w-3.5 text-[var(--color-ai)]" />
        <span className="text-[10px] font-medium text-[var(--color-ai)]">AI-generated advice</span>
      </div>

      {/* Recommendation badge */}
      <div className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-medium ${config.color}`}>
        <RecIcon className="h-4 w-4" />
        {config.label}
      </div>

      {/* Title */}
      <h4 className="mt-3 text-sm font-semibold text-foreground">{result.title}</h4>

      {/* Reasoning */}
      <p className="mt-2 text-sm text-muted-foreground leading-relaxed">{result.reasoning}</p>

      {/* Confidence */}
      <div className="mt-3 flex items-center gap-2">
        <span className="text-[10px] text-muted-foreground">Confidence</span>
        <div className="flex-1 h-1.5 rounded-full bg-muted overflow-hidden">
          <div
            className={`h-full rounded-full transition-all ${
              result.confidence >= 80 ? "bg-[var(--color-success)]/50" :
              result.confidence >= 50 ? "bg-[var(--color-warning)]/50" :
              "bg-[var(--color-destructive)]/50"
            }`}
            style={{ width: `${result.confidence}%` }}
          />
        </div>
        <span className="text-[10px] text-muted-foreground">{result.confidence}%</span>
      </div>

      {/* Action buttons */}
      {result.actions && result.actions.length > 0 && (
        <div className="mt-3 flex flex-wrap gap-2">
          {result.actions.map((action) => (
            <button
              key={action.label}
              onClick={() => {
                if (action.href) navigate(action.href);
              }}
              className="stagger-item inline-flex items-center gap-1.5 rounded-lg bg-[var(--color-primary)] px-3 py-1.5 text-xs font-medium text-white transition-colors hover:bg-[var(--color-primary)]/90"
            >
              {action.label}
              <ArrowRight className="h-3 w-3" />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
